import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Card, Col, Row, Table, Form, InputGroup, Nav, Spinner, Alert, Modal } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import Header from '../layouts/Header';
import Footer from '../layouts/Footer';
import { useLoading } from '../contexts/LoadingContext';
import axiosInstance from '../services/axiosInterceptor';
import { getToken } from '../services/authorize';
import { formatDateTH } from '../utils/dateUtils';

const API = process.env.REACT_APP_API;

// แท็บ 2 ภาค — endpoint คนละตัวแต่รูปแบบข้อมูลเหมือนกันทุกคอลัมน์
// (ดู reportsModel.getIslamicStudentAttendanceDetail ที่ alias ชื่อคอลัมน์ให้ตรงกัน)
const SECTIONS = [
  { key: 'islamic', label: 'ภาคศาสนา', icon: 'ri-book-open-line', endpoint: 'getIslamicStudentAttendanceDetail' },
  { key: 'general', label: 'ภาคสามัญ', icon: 'ri-government-line', endpoint: 'getStudentAttendanceDetail' },
];

const UPLOAD_BASE = (process.env.REACT_APP_API || 'http://localhost:3002/api').replace(/\/api\/?$/, '').replace(/\/$/, '');
const StudentAvatar = ({ photo, name, size = 28 }) => {
  const [hovered, setHovered] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const initial = name ? name.trim().charAt(0) : '?';
  const src = photo ? `${UPLOAD_BASE}${photo.startsWith('/') ? photo : `/${photo}`}` : null;

  const circleStyle = { width: size, height: size, borderRadius: '50%', flexShrink: 0 };
  return (
    <>
      <span
        style={{ position: 'relative', display: 'inline-block', lineHeight: 0 }}
        onMouseEnter={() => src && setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => src && setShowModal(true)}
      >
        {src
          ? <img src={src} alt="" style={{ ...circleStyle, objectFit: 'cover', cursor: 'pointer' }} />
          : <span style={{ ...circleStyle, backgroundColor: '#adb5bd', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.42, fontWeight: 600 }}>{initial}</span>
        }
        {src && hovered && (
          <div style={{ position: 'absolute', left: size + 6, top: '50%', transform: 'translateY(-50%)', zIndex: 1050, background: '#fff', borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.25)', padding: 4 }}>
            <img src={src} alt={name} style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 6, display: 'block' }} />
          </div>
        )}
      </span>
      {src && (
        <Modal show={showModal} onHide={() => setShowModal(false)} centered size="sm">
          <Modal.Body className="p-3 text-center">
            <img src={src} alt={name} style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 8 }} />
            {name && <div className="mt-2 fw-semibold small">{name}</div>}
          </Modal.Body>
        </Modal>
      )}
    </>
  );
};

const STATUS_META = {
  present: { label: 'มา', variant: 'success' },
  late: { label: 'สาย', variant: 'warning' },
  leave: { label: 'ลา', variant: 'info' },
  absent: { label: 'ขาด', variant: 'danger' },
};

const statusBadge = (status) => {
  const meta = STATUS_META[status] || { label: status || '-', variant: 'secondary' };
  return <Badge bg={meta.variant}>{meta.label}</Badge>;
};

const genderLabel = (gen) =>
  gen === 'm' ? <Badge bg="primary">ชาย</Badge> : <Badge bg="danger">หญิง</Badge>;

// การ์ดการเข้าเรียนรายวิชา 1 รายการ สำหรับมุมมองมือถือ
const SubjectAttSummaryCard = ({ s, rate }) => (
  <div className={`border rounded p-3 ${s.absent > 0 ? 'bg-danger-subtle' : ''}`}>
    <div className="fw-semibold">{s.subname ? <>{s.subid} {s.subname}</> : <span className="text-muted">-</span>}</div>
    <div className="fs-xs text-muted">{s.teachers || '-'}</div>
    <div className="d-flex flex-wrap gap-3 mt-2 pt-2 border-top fs-sm">
      <span className="text-danger fw-semibold">ขาด {s.absent}</span>
      <span>สาย {s.late}</span>
      <span>ลา {s.leave}</span>
      <span>มา {s.present}</span>
      <span className="text-muted">รวม {s.total} · เข้าเรียน {rate}%</span>
    </div>
  </div>
)

// การ์ดรายการเข้าเรียน 1 ครั้ง สำหรับมุมมองมือถือ
const AttDetailCard = ({ r }) => (
  <div className="border rounded p-3">
    <div className="d-flex align-items-center justify-content-between gap-2">
      <div className="fw-semibold">{formatDateTH(r.att_date)}</div>
      {statusBadge(r.att_status)}
    </div>
    <div className="mt-2 pt-2 border-top fs-sm">
      <div><span className="text-muted">คาบ:</span> {r.period_order || '-'}</div>
      <div><span className="text-muted">วิชา:</span> {r.sub_subname ? <>{r.sub_subid} {r.sub_subname}</> : <span className="text-muted">-</span>}</div>
      <div><span className="text-muted">ครูผู้สอน:</span> {r.t_name || <span className="text-muted">-</span>}</div>
      <div><span className="text-muted">หัวข้อที่สอน:</span> {r.tr_topic || <span className="text-muted fst-italic">ไม่ระบุ</span>}</div>
    </div>
  </div>
)

// สรุปสถานะรวมของรายการเข้าเรียนชุดหนึ่ง
const buildSummary = (rows) => {
  const acc = { present: 0, late: 0, leave: 0, absent: 0, total: 0 };
  rows.forEach((r) => {
    if (acc[r.att_status] !== undefined) acc[r.att_status] += 1;
    acc.total += 1;
  });
  acc.rate = acc.total > 0 ? Math.round(((acc.present + acc.late) / acc.total) * 100) : 0;
  return acc;
};

// จัดกลุ่มรายการเข้าเรียนเป็นรายวิชา เรียงวิชาที่ขาดมากที่สุดขึ้นก่อน
const buildSubjectSummary = (rows) => {
  const map = new Map();
  rows.forEach((r) => {
    const key = r.sub_subid || r.sub_subname || '-';
    if (!map.has(key)) {
      map.set(key, { subid: r.sub_subid, subname: r.sub_subname, teachers: new Set(), present: 0, late: 0, leave: 0, absent: 0, total: 0 });
    }
    const s = map.get(key);
    if (r.t_name) s.teachers.add(r.t_name);
    if (s[r.att_status] !== undefined) s[r.att_status] += 1;
    s.total += 1;
  });
  return Array.from(map.values())
    .map((s) => ({ ...s, teachers: Array.from(s.teachers).join(', ') }))
    .sort((a, b) => b.absent - a.absent || b.total - a.total);
};

// เนื้อหาของแท็บหนึ่งภาค — สรุปสถานะ, การเข้าเรียนรายวิชา และรายการเข้าเรียนทั้งหมด
const SectionPanel = ({ sectionLabel, rows, loaded, onExport }) => {
  const summary = useMemo(() => buildSummary(rows), [rows]);
  const subjectSummary = useMemo(() => buildSubjectSummary(rows), [rows]);
  const [subjectView, setSubjectView] = useState(() =>
    (typeof window !== 'undefined' && window.innerWidth < 768) ? 'card' : 'table');
  const [detailView, setDetailView] = useState(() =>
    (typeof window !== 'undefined' && window.innerWidth < 768) ? 'card' : 'table');

  return (
    <>
      <Row className="g-2 mb-3">
        {[
          { key: 'present', label: 'มา' },
          { key: 'late', label: 'สาย' },
          { key: 'leave', label: 'ลา' },
          { key: 'absent', label: 'ขาด' },
        ].map(({ key, label }) => (
          <Col xs={6} md={3} key={key}>
            <Card className="card-one">
              <Card.Body className="text-center p-3">
                <h3 className={`mb-0 text-${STATUS_META[key].variant}`}>{summary[key]}</h3>
                <small className="text-muted">{label}</small>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {subjectSummary.length > 0 && (
        <Card className="card-one mb-3">
          <Card.Header className="d-flex flex-wrap align-items-center gap-2">
            <Card.Title as="h6" className="mb-0">
              การเข้าเรียนรายวิชา
              <Badge bg="secondary" className="ms-2">{subjectSummary.length} วิชา</Badge>
            </Card.Title>
            <div className="btn-group btn-group-sm ms-auto" role="group" aria-label="สลับมุมมอง">
              <button type="button" className={`btn btn${subjectView === 'table' ? '' : '-outline'}-primary`}
                onClick={() => setSubjectView('table')} title="มุมมองตาราง">
                <i className="ri-table-line" />
              </button>
              <button type="button" className={`btn btn${subjectView === 'card' ? '' : '-outline'}-primary`}
                onClick={() => setSubjectView('card')} title="มุมมองการ์ด">
                <i className="ri-layout-grid-line" />
              </button>
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            {subjectView === 'card' ? (
              <div className="p-3">
                <Row className="g-3">
                  {subjectSummary.map((s, idx) => {
                    const rate = s.total > 0 ? Math.round(((s.present + s.late) / s.total) * 100) : 0;
                    return (
                      <Col key={idx} xs={12} md={6} xl={4}>
                        <SubjectAttSummaryCard s={s} rate={rate} />
                      </Col>
                    );
                  })}
                </Row>
              </div>
            ) : (
              <Table hover responsive className="mb-0 align-middle">
                <thead className="table-secondary">
                  <tr>
                    <th className="text-center" style={{ width: 50 }}>#</th>
                    <th>วิชา</th>
                    <th>ครูผู้สอน</th>
                    <th className="text-center text-danger" style={{ width: 70 }}>ขาด</th>
                    <th className="text-center text-warning" style={{ width: 70 }}>สาย</th>
                    <th className="text-center text-info" style={{ width: 70 }}>ลา</th>
                    <th className="text-center text-success" style={{ width: 70 }}>มา</th>
                    <th className="text-center" style={{ width: 80 }}>รวม</th>
                    <th className="text-center" style={{ width: 90 }}>เข้าเรียน</th>
                  </tr>
                </thead>
                <tbody>
                  {subjectSummary.map((s, idx) => {
                    const rate = s.total > 0 ? Math.round(((s.present + s.late) / s.total) * 100) : 0;
                    return (
                      <tr key={idx}>
                        <td className="text-center text-muted">{idx + 1}</td>
                        <td>{s.subname ? <>{s.subid} {s.subname}</> : <span className="text-muted">-</span>}</td>
                        <td>{s.teachers || <span className="text-muted">-</span>}</td>
                        <td className="text-center"><span className="text-danger fw-semibold">{s.absent}</span></td>
                        <td className="text-center">{s.late}</td>
                        <td className="text-center">{s.leave}</td>
                        <td className="text-center">{s.present}</td>
                        <td className="text-center text-muted">{s.total}</td>
                        <td className="text-center">{rate}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      )}

      <Card className="card-one mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <Card.Title as="h6" className="mb-0">
            รายการเข้าเรียน — {sectionLabel}
            <Badge bg="secondary" className="ms-2">{rows.length} ครั้ง</Badge>
            <span className="ms-2 text-muted fs-sm">เข้าเรียน {summary.rate}%</span>
          </Card.Title>
          {rows.length > 0 && (
            <div className="d-flex align-items-center gap-2">
              <div className="btn-group btn-group-sm" role="group" aria-label="สลับมุมมอง">
                <button type="button" className={`btn btn${detailView === 'table' ? '' : '-outline'}-primary`}
                  onClick={() => setDetailView('table')} title="มุมมองตาราง">
                  <i className="ri-table-line" />
                </button>
                <button type="button" className={`btn btn${detailView === 'card' ? '' : '-outline'}-primary`}
                  onClick={() => setDetailView('card')} title="มุมมองการ์ด">
                  <i className="ri-layout-grid-line" />
                </button>
              </div>
              <Button variant="outline-success" size="sm" onClick={() => onExport(summary)}>
                <i className="ri-file-excel-2-line me-1"></i>Excel
              </Button>
            </div>
          )}
        </Card.Header>
        <Card.Body className="p-0">
          {rows.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="ri-calendar-close-line fs-3 d-block mb-2"></i>
              {loaded ? `ไม่มีข้อมูลการเข้าเรียน${sectionLabel}ในช่วงที่เลือก` : 'กำลังโหลดข้อมูล...'}
            </div>
          ) : detailView === 'card' ? (
            <div className="p-3">
              <Row className="g-3">
                {rows.map((r, idx) => (
                  <Col key={idx} xs={12} md={6} xl={4}>
                    <AttDetailCard r={r} />
                  </Col>
                ))}
              </Row>
            </div>
          ) : (
            <Table hover responsive className="mb-0 align-middle">
              <thead className="table-secondary">
                <tr>
                  <th className="text-center" style={{ width: 50 }}>#</th>
                  <th className="text-center" style={{ width: 120 }}>วันที่</th>
                  <th className="text-center" style={{ width: 60 }}>คาบ</th>
                  <th>วิชา</th>
                  <th>ครูผู้สอน</th>
                  <th>หัวข้อที่สอน</th>
                  <th className="text-center" style={{ width: 80 }}>สถานะ</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => (
                  <tr key={idx}>
                    <td className="text-center text-muted">{idx + 1}</td>
                    <td className="text-center">{formatDateTH(r.att_date)}</td>
                    <td className="text-center">{r.period_order || '-'}</td>
                    <td>{r.sub_subname ? <>{r.sub_subid} {r.sub_subname}</> : <span className="text-muted">-</span>}</td>
                    <td>{r.t_name || <span className="text-muted">-</span>}</td>
                    <td>{r.tr_topic || <span className="text-muted fst-italic">ไม่ระบุ</span>}</td>
                    <td className="text-center">{statusBadge(r.att_status)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

export default function StudentAttendanceIndividual() {
  const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
  const [skin, setSkin] = useState(currentSkin);
  const { setLoading } = useLoading();

  const switchSkin = (s) => {
    const tables = document.getElementsByTagName('table');
    for (const t of tables) {
      const theads = t.querySelectorAll('thead');
      if (s === 'dark') theads.forEach((th) => th.classList.add('table-dark'));
      else theads.forEach((th) => th.classList.remove('table-dark'));
    }
  };
  useEffect(() => { switchSkin(skin); });

  const [yearId, setYearId] = useState(null);
  const [activeTab, setActiveTab] = useState('islamic');

  const [searchKey, setSearchKey] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const inputRef = useRef(null);

  // ข้อมูลทั้งสองภาคถูกโหลดพร้อมกัน สลับแท็บจึงไม่ต้องรอโหลดใหม่
  const [rowsBySection, setRowsBySection] = useState({ islamic: [], general: [] });
  const [loadedSections, setLoadedSections] = useState({ islamic: false, general: false });

  useEffect(() => {
    axiosInstance
      .get(`${API}/getLastestStudyYear`, { headers: { authorization: `Bearer ${getToken()}` } })
      .then((res) => { if (res.data?.year_id) setYearId(res.data.year_id); })
      .catch(() => {});
  }, []);

  const handleSearch = async (e) => {
    e.preventDefault();
    const key = searchKey.trim();
    if (key.length < 2) { setError('กรุณากรอกคำค้นหาอย่างน้อย 2 ตัวอักษร'); return; }
    setError('');
    setSearching(true);
    setSearched(false);
    setSearchResults([]);
    try {
      const res = await axiosInstance.get(`/api/students/search?q=${encodeURIComponent(key)}`);
      if (res.data.success) { setSearchResults(res.data.data); setSearched(true); }
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการค้นหา');
    } finally {
      setSearching(false);
    }
  };

  // โหลดการเข้าเรียนทั้งสองภาคของนักเรียนที่เลือก
  const loadStudentDetail = useCallback((student) => {
    if (!yearId || !student) return;
    setLoading(true);
    setLoadedSections({ islamic: false, general: false });
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    Promise.allSettled(
      SECTIONS.map((sec) =>
        axiosInstance.get(`${API}/${sec.endpoint}/${student.st_id}/${yearId}?${params}`, {
          headers: { authorization: `Bearer ${getToken()}` },
        })
      )
    )
      .then((results) => {
        const next = {};
        SECTIONS.forEach((sec, i) => {
          next[sec.key] = results[i].status === 'fulfilled' ? (results[i].value.data || []) : [];
        });
        setRowsBySection(next);
      })
      .finally(() => {
        setLoading(false);
        setLoadedSections({ islamic: true, general: true });
      });
  }, [yearId, startDate, endDate, setLoading]);

  const selectStudent = (student) => {
    setSelectedStudent(student);
    setSearchResults([]);
    setSearched(false);
    loadStudentDetail(student);
  };

  // โหลดใหม่เมื่อเปลี่ยนช่วงวันที่ (หรือเมื่อได้ปีการศึกษาหลังเลือกนักเรียนแล้ว)
  useEffect(() => {
    if (selectedStudent && yearId) loadStudentDetail(selectedStudent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, yearId]);

  const handleExportExcel = (sectionKey, summary) => {
    if (!selectedStudent) return;
    const section = SECTIONS.find((s) => s.key === sectionKey);
    const rows = rowsBySection[sectionKey] || [];
    const wb = XLSX.utils.book_new();
    const aoa = [
      [`รายงานการเข้าเรียนรายบุคคล — ${section.label}`],
      [`${selectedStudent.st_idnumber} ${selectedStudent.st_name}`],
      [`ช่วงวันที่: ${startDate || 'ทั้งหมด'} ถึง ${endDate || 'ทั้งหมด'}`],
      [`สรุป: มา ${summary.present} / สาย ${summary.late} / ลา ${summary.leave} / ขาด ${summary.absent} (เข้าเรียน ${summary.rate}%)`],
      [],
      ['#', 'วันที่', 'คาบ', 'วิชา', 'ครูผู้สอน', 'หัวข้อที่สอน', 'สถานะ'],
    ];
    rows.forEach((r, idx) => {
      aoa.push([
        idx + 1,
        formatDateTH(r.att_date),
        r.period_order || '-',
        r.sub_subname ? `${r.sub_subid || ''} ${r.sub_subname}`.trim() : '-',
        r.t_name || '-',
        r.tr_topic || '-',
        (STATUS_META[r.att_status] || {}).label || r.att_status,
      ]);
    });
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [{ wch: 5 }, { wch: 14 }, { wch: 6 }, { wch: 28 }, { wch: 22 }, { wch: 36 }, { wch: 8 }];
    XLSX.utils.book_append_sheet(wb, ws, section.label);
    XLSX.writeFile(wb, `การเข้าเรียน_${section.label}_${selectedStudent.st_idnumber}.xlsx`);
  };

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">ฝ่ายปกครอง</Link></li>
              <li className="breadcrumb-item active" aria-current="page">การเข้าเรียนรายบุคคล</li>
            </ol>
            <h4 className="main-title mb-0">การเข้าเรียนรายบุคคล</h4>
          </div>
        </div>

        <Card className="card-one mb-3">
          <Card.Body>
            {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
            <Form onSubmit={handleSearch}>
              <Form.Label><strong>ค้นหานักเรียน</strong></Form.Label>
              <Row className="g-2">
                <Col md={6}>
                  <InputGroup>
                    <Form.Control
                      ref={inputRef}
                      type="text"
                      value={searchKey}
                      onChange={(e) => setSearchKey(e.target.value)}
                      placeholder="ค้นหาด้วยเลขประจำตัวหรือชื่อ-สกุล..."
                    />
                    <Button variant="primary" type="submit" disabled={searching}>
                      {searching ? <Spinner animation="border" size="sm" /> : <><i className="ri-search-2-line me-1"></i>ค้นหา</>}
                    </Button>
                  </InputGroup>
                </Col>
                <Col md={3}>
                  <Form.Control type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} title="ตั้งแต่วันที่" />
                </Col>
                <Col md={3}>
                  <Form.Control type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} title="ถึงวันที่" />
                </Col>
              </Row>
            </Form>

            {searched && searchResults.length === 0 && (
              <Alert variant="warning" className="mt-3 mb-0">ไม่พบนักเรียนที่ค้นหา</Alert>
            )}
            {searchResults.length > 0 && (
              <div className="mt-3 d-flex flex-wrap gap-2">
                {searchResults.map((st) => (
                  <Button key={st.st_id} variant="outline-primary" size="sm" onClick={() => selectStudent(st)}>
                    {st.st_name} <span className="text-muted">({st.st_idnumber})</span>
                  </Button>
                ))}
              </div>
            )}
          </Card.Body>
        </Card>

        {selectedStudent && (
          <>
            <Row className="g-3 mb-3">
              <Col xl={12}>
                <Card className="card-one">
                  <Card.Body className="p-3 d-flex flex-wrap align-items-center gap-3">
                    <StudentAvatar photo={selectedStudent.st_photo} name={selectedStudent.st_name} size={48} />
                    <div>
                      <span className="fw-semibold fs-6">{selectedStudent.st_name}</span>
                      <span className="text-muted ms-2">{selectedStudent.st_idnumber}</span>
                    </div>
                    {selectedStudent.st_gen && genderLabel(selectedStudent.st_gen)}
                    {selectedStudent.c_class && selectedStudent.gen_room_no && (
                      <Badge bg="info">ภาคสามัญ ม.{selectedStudent.c_class}/{selectedStudent.gen_room_no}</Badge>
                    )}
                    {selectedStudent.ic_class && selectedStudent.isl_room_no && (
                      <Badge bg="success">ภาคศาสนา {selectedStudent.ic_class}/{selectedStudent.isl_room_no}</Badge>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Nav variant="tabs" className="mb-3" activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
              {SECTIONS.map((sec) => (
                <Nav.Item key={sec.key}>
                  <Nav.Link eventKey={sec.key}>
                    <i className={`${sec.icon} me-1`}></i>{sec.label}
                    {loadedSections[sec.key] && (
                      <Badge bg="secondary" className="ms-2">{(rowsBySection[sec.key] || []).length}</Badge>
                    )}
                  </Nav.Link>
                </Nav.Item>
              ))}
            </Nav>

            {SECTIONS.map((sec) => (
              activeTab === sec.key && (
                <SectionPanel
                  key={sec.key}
                  sectionLabel={sec.label}
                  rows={rowsBySection[sec.key] || []}
                  loaded={loadedSections[sec.key]}
                  onExport={(summary) => handleExportExcel(sec.key, summary)}
                />
              )
            ))}
          </>
        )}

        <Footer />
      </div>
    </React.Fragment>
  );
}
